import React from 'react'

import {noop} from 'es-toolkit'
import {isEmpty} from 'es-toolkit/compat'
import {useFormik} from 'formik'

import {useAppDispatch} from '../../../hooks/redux'
import {fatalError} from '../../../redux/features/core'
import {
  closeModal,
  openAddProviderInfoPricingModal,
  openCreatePricingModal,
} from '../../../redux/features/modal'

import useAsync from '../../../../../commons/src/hooks/use-async'
import useKeyPress from '../../../../../commons/src/hooks/use-key-press'
import preventDoubleClick from '../../../../../commons/src/services/prevent-double-click'
import * as priceApi from '../../../services/api/price'
import * as providerInfoApi from '../../../services/api/provider-info'
import {toTouchedFormData, toValidationSchema} from '../../utils/form'

import {ModalMeta, Price, PriceId, ProviderId, ProviderInfo} from '../../../types'

import Button from '../../../../../commons/src/components/button'
import FieldNote from '../../../../../commons/src/components/field-note'
import FormRow from '../../../../../commons/src/components/form-row'
import Headline from '../../../../../commons/src/components/headline'
import Link from '../../../../../commons/src/components/link'
import Modal from '../../../../../commons/src/components/modal'
import Paragraph from '../../../../../commons/src/components/paragraph'
import SelectField from '../../../../../commons/src/components/select-field'
import SelectMenu from '../../../../../commons/src/components/select-menu'

import {
  PriceOption,
  SingleFormData,
  initialValues,
  validationSchema,
} from '../../provider-info/edit-pricing-partial'

type Props = {
  providerId: ProviderId
  providerInfos: ProviderInfo[]
  initialPriceId: PriceId
  meta: ModalMeta
  onDone?: () => void
}

const AddProviderInfoPricingModal = ({
  meta,
  onDone = noop,
  providerId,
  providerInfos,
  initialPriceId,
}: Props) => {
  const dispatch = useAppDispatch()

  const {isLoading: isLoadingPrices, data: prices = []} = useAsync(
    () => priceApi.getByProvider(providerId),
    [],
    {
      onError: error => dispatch(fatalError({error})),
    }
  )

  const formik = useFormik<SingleFormData>({
    initialValues,
    validationSchema: toValidationSchema(validationSchema),
    onSubmit: async (values, actions) => {
      const providerInfoRequest = toTouchedFormData(
        values,
        formik.initialValues
      ) as Partial<SingleFormData>

      try {
        const providerInfoIds = providerInfos.map(info => info.id)
        await providerInfoApi.patchMultiple(providerInfoRequest, providerInfoIds)

        onDone()
        dispatch(closeModal())
      } catch (error) {
        actions.setFieldError('formikGeneral', (error as Error).message)
      }
    },
  })

  React.useEffect(() => {
    if (initialPriceId) {
      formik.setFieldValue('priceId', initialPriceId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPriceId])

  const setCreatedPricing = (price: Price) => {
    // Reopen self with initial priceId onDone
    dispatch(
      openAddProviderInfoPricingModal({providerId, providerInfos, onDone, initialPriceId: price.id})
    )
  }

  useKeyPress(
    preventDoubleClick(() => formik.handleSubmit()),
    'Enter'
  )

  const priceOptions: PriceOption[] = prices.map(({id, name}) => ({
    label: name,
    value: id,
  }))

  return (
    <Modal
      fullscreenMobile={meta.fullscreenMobile}
      headline={<Headline label="Add pricing" size="l" />}
      onClose={() => dispatch(closeModal())}
      buttons={
        <>
          <Button hierarchy="ghost" onClick={() => dispatch(closeModal())}>
            Cancel
          </Button>
          <Button
            disabled={formik.isSubmitting || (formik.submitCount > 0 && !isEmpty(formik.errors))}
            onClick={preventDoubleClick(() => formik.handleSubmit())}
          >
            Save
          </Button>
        </>
      }
    >
      <form onSubmit={formik.handleSubmit}>
        {formik.errors.formikGeneral && <Paragraph error>{formik.errors.formikGeneral}</Paragraph>}
        <Paragraph>
          You can either select one of your existing pricings or you can{' '}
          <Link onClick={() => dispatch(openCreatePricingModal({onDone: setCreatedPricing}))}>
            create a new one
          </Link>
          .
        </Paragraph>
        <FormRow>
          <FieldNote appearance="error" note={formik.submitCount > 0 && formik.errors.priceId}>
            <SelectField
              name="type"
              disabled={isLoadingPrices}
              placeholder={isLoadingPrices ? 'Loading pricings...' : 'Select pricing'}
              value={priceOptions.find(({value}) => value === formik.values.priceId)}
              menu={<SelectMenu values={priceOptions} />}
              onChange={option => {
                formik.setFieldValue('priceId', (option as PriceOption).value)
              }}
              onBlur={formik.handleBlur}
            />
          </FieldNote>
        </FormRow>
      </form>
    </Modal>
  )
}

export default AddProviderInfoPricingModal
